/**
 * 地図マーカー用アイコン画像の生成 + MapLibre への登録ユーティリティ (GIS / 投稿 共通基盤)。
 *
 * MapLibre の line/circle レイヤーは矢印や絵文字を描けないため、canvas に描いて
 * `map.addImage` し `icon-image` を持つ symbol レイヤーで表示する。色 (矢印) / 絵文字は
 * 内容が確定しているため SDF (再着色) は使わず、確定色・カラー絵文字の画像を直接登録する。
 *
 * 用途:
 *   - `createArrowIconImage(color)`  … GIS 線の方向矢印 (ArrowLine)
 *   - `createEmojiIconImage(emoji)`  … 投稿のプロットマーカー絵文字 (ColorRule.icon) / GIS フォントマーカー
 *   - `syncMapImages(host, …)`       … 画像 set の add/remove ライフサイクル管理 (重複登録・撤去漏れ防止)
 *
 * maplibre 非依存 (DOM canvas のみ)。canvas 非対応環境 (SSR / 一部 jsdom) では null を返し、
 * 呼び出し側は addImage をスキップする。
 */
export interface MapIconImage {
  width: number;
  height: number;
  data: Uint8Array;
}

/** 後方互換エイリアス (旧 gisArrowIcon の型名)。 */
export type ArrowIconImage = MapIconImage;

function makeCanvas(
  size: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === 'undefined') return null;
  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement('canvas');
  } catch {
    return null;
  }
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null; // jsdom 等で 2d context 非対応
  ctx.clearRect(0, 0, size, size);
  return { canvas, ctx };
}

function toImage(ctx: CanvasRenderingContext2D, size: number): MapIconImage | null {
  let img: ImageData;
  try {
    img = ctx.getImageData(0, 0, size, size);
  } catch {
    return null;
  }
  return { width: size, height: size, data: new Uint8Array(img.data) };
}

/**
 * GIS 線の方向矢印用の矢じりアイコン (右向き三角形、指定色)。
 * symbol-placement:line で線の進行方向に合わせて回転する前提 (右向き=線方向)。
 */
export function createArrowIconImage(color: string, scale = 1): MapIconImage | null {
  const size = Math.max(12, Math.round(28 * (scale > 0 ? scale : 1)));
  const c = makeCanvas(size);
  if (!c) return null;
  const { ctx } = c;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(size * 0.18, size * 0.24);
  ctx.lineTo(size * 0.86, size * 0.5);
  ctx.lineTo(size * 0.18, size * 0.76);
  ctx.closePath();
  ctx.fill();
  return toImage(ctx, size);
}

/** 図形マーカーの種別。 */
type MarkerShape =
  | 'heart'
  | 'star'
  | 'triUp'
  | 'triDown'
  | 'circle'
  | 'square'
  | 'diamond'
  | 'plus';

/**
 * よく使うマーカー文字 → 図形 + 塗り(filled)/輪郭(outline) の対応。
 * これらは OS フォントに頼らず canvas で図形を直接描くため、**どの端末でも確実に表示・着色**できる。
 */
const SHAPE_FOR_CHAR: Record<string, { shape: MarkerShape; filled: boolean }> = {
  '♥': { shape: 'heart', filled: true },
  '❤': { shape: 'heart', filled: true },
  '♡': { shape: 'heart', filled: false },
  '★': { shape: 'star', filled: true },
  '☆': { shape: 'star', filled: false },
  '▲': { shape: 'triUp', filled: true },
  '△': { shape: 'triUp', filled: false },
  '▼': { shape: 'triDown', filled: true },
  '▽': { shape: 'triDown', filled: false },
  '●': { shape: 'circle', filled: true },
  '⬤': { shape: 'circle', filled: true },
  '○': { shape: 'circle', filled: false },
  '■': { shape: 'square', filled: true },
  '□': { shape: 'square', filled: false },
  '◆': { shape: 'diamond', filled: true },
  '◇': { shape: 'diamond', filled: false },
  '✚': { shape: 'plus', filled: true },
  '＋': { shape: 'plus', filled: true },
  '➕': { shape: 'plus', filled: true },
};

/** IconPicker 用のカテゴリ別マーカー記号。 */
export interface MarkerIconGroup {
  label: string;
  chars: readonly string[];
}

/**
 * IconPicker のカテゴリ別マーカー記号。**protomaps の Noto Sans Regular グリフに収録済を実測検証**した
 * BMP 記号のみ。投稿マーカーは text-field + `text-font: ['Noto Sans Regular']` + `text-color` で描くため、
 * これらは **OS 非依存で確実に表示され cell の色で着色**される。補助面の絵文字 (💔 等 U+1F494) は glyph
 * 範囲外で表示不可。ここに無い文字も `isGlyphAvailable()` で確認すれば使える。
 */
export const MARKER_ICON_GROUPS: readonly MarkerIconGroup[] = [
  {
    label: '図形',
    chars: [
      '●',
      '○',
      '◉',
      '◐',
      '◑',
      '■',
      '□',
      '▣',
      '▤',
      '▦',
      '▲',
      '△',
      '▼',
      '▽',
      '◀',
      '▶',
      '◆',
      '◇',
      '◈',
      '⬛',
      '⬜',
      '⬢',
      '⬣',
      '⬟',
      '⬠',
      '⬤',
      '⯀',
      '▰',
      '▱',
    ],
  },
  {
    label: '星・ハート',
    chars: [
      '★',
      '☆',
      '⭐',
      '✦',
      '✧',
      '✩',
      '✪',
      '✫',
      '✬',
      '✭',
      '✮',
      '✯',
      '✰',
      '❂',
      '❃',
      '♥',
      '❤',
      '♡',
      '❥',
      '❣',
      '❦',
      '❧',
      '♦',
      '♢',
      '♣',
      '♧',
      '♠',
      '♤',
    ],
  },
  {
    label: '顔・天気・花',
    chars: [
      '☺',
      '☹',
      '☻',
      '☀',
      '☁',
      '☂',
      '☃',
      '☄',
      '☼',
      '☽',
      '⛄',
      '⛅',
      '⛈',
      '☔',
      '❄',
      '❅',
      '❆',
      '✿',
      '❀',
      '❁',
      '☘',
      '⚜',
    ],
  },
  {
    label: '記号',
    chars: [
      '⚠',
      '⚡',
      '⛔',
      '✔',
      '✓',
      '✖',
      '✗',
      '✚',
      '✛',
      '✜',
      '☑',
      '☒',
      '☐',
      '❗',
      '❓',
      '♻',
      '⚐',
      '⚑',
      '⚒',
      '⚓',
      '⚔',
      '⚙',
      '⚖',
      '⛳',
      '⚛',
      '☎',
      '✆',
      '✉',
      '✈',
      '⛵',
      '⛺',
      '⛽',
      '⛏',
      '☕',
    ],
  },
  {
    label: '矢印',
    chars: [
      '←',
      '↑',
      '→',
      '↓',
      '↔',
      '↕',
      '↖',
      '↗',
      '↘',
      '↙',
      '➡',
      '⬅',
      '⬆',
      '⬇',
      '➤',
      '➜',
      '➥',
      '➦',
      '➧',
      '⇦',
      '⇧',
      '⇨',
      '⇩',
      '⮕',
      '⭢',
    ],
  },
];

/** 全マーカー記号 (グループを平坦化、後方互換)。 */
export const MARKER_ICON_CHOICES: readonly string[] = MARKER_ICON_GROUPS.flatMap((g) => g.chars);

// ── glyph 可用性チェック (入力した文字が Noto Sans に有るかをライブ判定) ───────────────
const PROTOMAPS_GLYPH_BASE = 'https://protomaps.github.io/basemaps-assets/fonts';
/** fontstack/rangeStart → 収録コードポイント集合 の取得 Promise (range 単位でキャッシュ)。 */
const glyphRangeCache = new Map<string, Promise<Set<number>>>();

/** glyph PBF (Mapbox glyphs proto) をデコードし収録コードポイントを返す (依存なしの最小 walker)。 */
function decodeGlyphCodepoints(buf: Uint8Array): Set<number> {
  const out = new Set<number>();
  const walk = (
    start: number,
    end: number,
    cb: (field: number, wt: number, a: number, b: number) => void,
  ) => {
    let i = start;
    while (i < end) {
      let tag = 0;
      let s = 0;
      let b: number;
      do {
        b = buf[i++]!;
        tag |= (b & 0x7f) << s;
        s += 7;
      } while (b & 0x80);
      const field = tag >>> 3;
      const wt = tag & 7;
      if (wt === 0) {
        let v = 0;
        let s2 = 0;
        let bb: number;
        do {
          bb = buf[i++]!;
          v += (bb & 0x7f) * 2 ** s2;
          s2 += 7;
        } while (bb & 0x80);
        cb(field, wt, v, 0);
      } else if (wt === 2) {
        let len = 0;
        let s2 = 0;
        let bb: number;
        do {
          bb = buf[i++]!;
          len |= (bb & 0x7f) << s2;
          s2 += 7;
        } while (bb & 0x80);
        cb(field, wt, i, i + len);
        i += len;
      } else if (wt === 5) i += 4;
      else if (wt === 1) i += 8;
      else break;
    }
  };
  // glyphs{ stacks=1 fontstack{ glyphs=3 glyph{ id=1 varint } } }
  walk(0, buf.length, (f1, w1, a1, b1) => {
    if (f1 === 1 && w1 === 2)
      walk(a1, b1, (f2, w2, a2, b2) => {
        if (f2 === 3 && w2 === 2)
          walk(a2, b2, (f3, w3, v3) => {
            if (f3 === 1 && w3 === 0) out.add(v3);
          });
      });
  });
  return out;
}

/**
 * 入力した文字が text-field 描画に使う Noto Sans グリフに収録されているか判定する
 * (= 地図で確実に表示できるか)。glyph PBF を range 単位で取得・キャッシュ。
 * 補助面 (>U+FFFF、絵文字等) や合字/異体字付きは glyph 範囲外のため false。
 */
export async function isGlyphAvailable(
  char: string,
  fontstack = 'Noto Sans Regular',
): Promise<boolean> {
  if (!char) return false;
  if (Array.from(char).length !== 1) return false; // 単一コードポイントのみ
  const cp = char.codePointAt(0);
  if (cp === undefined || cp > 0xffff) return false; // glyph は BMP のみ
  const start = Math.floor(cp / 256) * 256;
  const key = `${fontstack}/${start}`;
  let p = glyphRangeCache.get(key);
  if (!p) {
    p = (async () => {
      try {
        const res = await fetch(
          `${PROTOMAPS_GLYPH_BASE}/${encodeURIComponent(fontstack)}/${start}-${start + 255}.pbf`,
        );
        if (!res.ok) return new Set<number>();
        return decodeGlyphCodepoints(new Uint8Array(await res.arrayBuffer()));
      } catch {
        return new Set<number>();
      }
    })();
    glyphRangeCache.set(key, p);
  }
  return (await p).has(cp);
}

function buildShapePath(
  ctx: CanvasRenderingContext2D,
  shape: MarkerShape,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      break;
    case 'square':
      ctx.rect(cx - r * 0.86, cy - r * 0.86, r * 1.72, r * 1.72);
      break;
    case 'triUp':
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r * 0.92, cy + r * 0.66);
      ctx.lineTo(cx - r * 0.92, cy + r * 0.66);
      ctx.closePath();
      break;
    case 'triDown':
      ctx.moveTo(cx, cy + r);
      ctx.lineTo(cx + r * 0.92, cy - r * 0.66);
      ctx.lineTo(cx - r * 0.92, cy - r * 0.66);
      ctx.closePath();
      break;
    case 'diamond':
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r * 0.82, cy);
      ctx.lineTo(cx, cy + r);
      ctx.lineTo(cx - r * 0.82, cy);
      ctx.closePath();
      break;
    case 'plus': {
      const w = r * 0.34;
      ctx.rect(cx - w, cy - r, w * 2, r * 2);
      ctx.rect(cx - r, cy - w, r * 2, w * 2);
      break;
    }
    case 'star': {
      const outer = r;
      const inner = r * 0.42;
      for (let k = 0; k < 10; k += 1) {
        const rr = k % 2 === 0 ? outer : inner;
        const a = -Math.PI / 2 + (k * Math.PI) / 5;
        const x = cx + rr * Math.cos(a);
        const y = cy + rr * Math.sin(a);
        if (k === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
    case 'heart': {
      const top = cy - r * 0.35;
      ctx.moveTo(cx, cy + r * 0.78);
      ctx.bezierCurveTo(cx - r * 1.2, cy - r * 0.1, cx - r * 0.55, top - r * 0.95, cx, top);
      ctx.bezierCurveTo(
        cx + r * 0.55,
        top - r * 0.95,
        cx + r * 1.2,
        cy - r * 0.1,
        cx,
        cy + r * 0.78,
      );
      ctx.closePath();
      break;
    }
  }
}

/**
 * 投稿マーカー / GIS フォントマーカー用のアイコン。
 *
 * 描画方針:
 *   1. **既知の図形文字** (♥ ★ ▲ ● ■ ◆ ✚ 等、`SHAPE_FOR_CHAR`) は OS フォントに頼らず canvas で
 *      図形を直接描く → どの端末でも確実に表示でき、`color` で確実に着色される (filled=塗り / outline=輪郭)。
 *      → 「色を付けたいマーカー」はこれらの記号を使うのが確実。
 *   2. それ以外の文字 (任意の絵文字・文字) はフォント描画に fallback。**単色記号**は `color` で着色されるが、
 *      **カラー絵文字** (❤️/💚/⚠️ 等) は OS が多色固定で描き fillStyle を無視するため native 色のまま。
 */
export function createEmojiIconImage(
  emoji: string,
  color?: string,
  scale = 1,
): MapIconImage | null {
  if (!emoji) return null;
  const size = Math.max(16, Math.round(36 * (scale > 0 ? scale : 1)));
  const c = makeCanvas(size);
  if (!c) return null;
  const { ctx } = c;
  const col = color || '#1f2937';

  const def = SHAPE_FOR_CHAR[emoji];
  if (def) {
    const r = size * 0.36;
    buildShapePath(ctx, def.shape, size / 2, size / 2, r);
    if (def.filled) {
      ctx.fillStyle = col;
      ctx.fill();
    } else {
      ctx.strokeStyle = col;
      ctx.lineWidth = Math.max(2, size * 0.1);
      ctx.stroke();
    }
    return toImage(ctx, size);
  }

  // fallback: フォント描画。記号フォントを優先し (単色記号を着色可に)、無ければ絵文字フォント (native 色)。
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${Math.round(size * 0.74)}px "Segoe UI Symbol","Apple Symbols","Noto Sans Symbols2","Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",sans-serif`;
  ctx.fillStyle = col;
  ctx.fillText(emoji, size / 2, size / 2 + size * 0.04);
  return toImage(ctx, size);
}

/** `syncMapImages` が要求する MapLibre `Map` の構造的部分型 (maplibre 非依存)。 */
export interface MapImageHost {
  hasImage(id: string): boolean;
  addImage(id: string, image: MapIconImage): void;
  removeImage(id: string): void;
  listImages(): string[];
}

/**
 * 画像 set を map に同期する (add 不足分 / remove 不要分)。再描画のたびに呼んでも
 * 重複登録・撤去漏れが起きないようにする共通 glue。
 *
 * @param host    MapLibre Map (構造的に MapImageHost を満たす)
 * @param entries 望ましい画像 (image=null はスキップ)
 * @param ownsId  この同期が「所有」する id 判定 (例 id => id.startsWith('pin-'))。
 *                所有かつ不要になった既存画像のみ removeImage する (他機能の画像は触らない)。
 */
export function syncMapImages(
  host: MapImageHost,
  entries: Array<{ id: string; image: MapIconImage | null }>,
  ownsId: (id: string) => boolean,
): void {
  const desired = new Set(entries.map((e) => e.id));
  let existing: string[] = [];
  try {
    existing = host.listImages();
  } catch {
    existing = [];
  }
  for (const id of existing) {
    if (ownsId(id) && !desired.has(id)) {
      try {
        if (host.hasImage(id)) host.removeImage(id);
      } catch {
        /* 破棄後などは silent */
      }
    }
  }
  for (const e of entries) {
    if (!e.image) continue;
    try {
      if (!host.hasImage(e.id)) host.addImage(e.id, e.image);
    } catch {
      /* canvas 非対応 / 競合は silent */
    }
  }
}
